import { addDays, parseISO, subDays } from 'date-fns';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { MultiFilter, MultiFilterSection } from '@/components/MultiFilter';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDate, titleCase, todayDate } from 'utils';

import { RequestStatus } from '../../../cip/_openapi/portfolio-optimizer';

type FilterType = 'status' | 'date-range';

const InputName = {
    investmentRequestStates: 'investmentRequestStates',
    startDate: 'startDate',
    endDate: 'endDate',
} as const;

const requestStatusOptions = Object.values(RequestStatus).map((status) => ({
    value: status,
    label: titleCase(status),
}));

const TODAY = todayDate();
export const DEFAULT_START_DATE = subDays(TODAY, 7);
export const DEFAULT_END_DATE = addDays(TODAY, 30);

const InvestmentRequestStatusFilter = () => {
    const { setValue } = useFormContext();
    const selectedStates: RequestStatus[] = useWatch({ name: InputName.investmentRequestStates }) ?? [];

    useEffect(() => {
        localStorage.setItem('investmentRequestStates', JSON.stringify(selectedStates));
    }, [selectedStates]);

    return (
        <>
            {requestStatusOptions.map((option) => {
                const isSelected = selectedStates.includes(option.value);
                return (
                    <div
                        key={option.value}
                        className="flex cursor-pointer items-center space-x-3 rounded p-2 hover:bg-gray-50"
                        onClick={() =>
                            setValue(
                                InputName.investmentRequestStates,
                                isSelected
                                    ? selectedStates.filter((s) => s !== option.value)
                                    : [...selectedStates, option.value],
                            )
                        }
                    >
                        <Checkbox checked={isSelected} />
                        <span>{option.label}</span>
                    </div>
                );
            })}
        </>
    );
};

const DateRangeFilter = () => {
    const { control } = useFormContext();
    return (
        <div className="flex flex-col gap-4 p-2">
            <div className="flex flex-col gap-1.5">
                <Label>From date</Label>
                <Controller
                    control={control}
                    name={InputName.startDate}
                    render={({ field }) => (
                        <Input
                            type="date"
                            value={formatDate(field.value, 'yyyy-MM-dd')}
                            onChange={(e) => field.onChange(e.target.value ? parseISO(e.target.value) : undefined)}
                        />
                    )}
                />
            </div>
            <div className="flex flex-col gap-1.5">
                <Label>To date</Label>
                <Controller
                    control={control}
                    name={InputName.endDate}
                    render={({ field }) => (
                        <Input
                            type="date"
                            value={formatDate(field.value, 'yyyy-MM-dd')}
                            onChange={(e) => field.onChange(e.target.value ? parseISO(e.target.value) : undefined)}
                        />
                    )}
                />
            </div>
        </div>
    );
};

export const InvestmentRequestMultiFilter = () => {
    const {
        resetField,
        setValue,
        formState: { dirtyFields },
    } = useFormContext();
    const investmentRequestStates: RequestStatus[] = useWatch({ name: InputName.investmentRequestStates }) ?? [];

    const sections: MultiFilterSection<FilterType>[] = [
        {
            id: 'status',
            label: 'Status',
            count: investmentRequestStates.length,
            onClear: () => {
                setValue(InputName.investmentRequestStates, []);
                localStorage.removeItem('investmentRequestStates');
            },
            content: <InvestmentRequestStatusFilter />,
        },
        {
            id: 'date-range',
            label: 'Date Range',
            count: [dirtyFields.startDate, dirtyFields.endDate].filter(Boolean).length,
            onClear: () => {
                resetField(InputName.startDate);
                resetField(InputName.endDate);
            },
            content: <DateRangeFilter />,
        },
    ];

    return (
        <div className="relative">
            <MultiFilter
                sections={sections}
                defaultSectionId="status"
                dropdownClassName="h-80 w-96 p-0"
                contentClassName="min-h-0 flex-1 overflow-y-auto p-2"
            />
        </div>
    );
};
